import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, Alert, AlertTitle, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Grid, Divider, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, FormControlLabel, Switch, useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { API_ENDPOINTS } from '../../config/api';
import { TOOL_COLORS } from '../../constants/colors';

const ACCENT = TOOL_COLORS.OTHER.primary;

// The ClickHouse types a parameter can sensibly take. The type goes into the
// {name:Type} placeholder, so it has to be one ClickHouse recognises.
const PARAM_TYPES = ['Date', 'DateTime', 'String', 'UInt8', 'UInt16', 'UInt32', 'UInt64', 'Int32', 'Int64', 'Float64', 'Bool'];

const CATEGORIES = ['Sales', 'Product content', 'Data quality', 'Inventory', 'Custom'];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('jwtToken')}` });

const emptyReport = () => ({
  key: '', name: '', description: '', category: 'Custom', sql: '',
  parameters: [], columns: [], enabled: true, request: '',
});

/**
 * Build a report by describing it, or by writing the SQL.
 *
 * The flow is deliberately "propose, then prove": the assistant drafts against
 * the live schema, and the answer that matters comes from the server's
 * validation - a DESCRIBE the warehouse has to accept. So the dialog never
 * treats a draft as finished; it shows the issues, lets the SQL and parameters
 * be edited, and re-checks. Save is disabled until the server says it runs.
 */
const ReportBuilderDialog = ({ open, report, onClose, onSaved, notify }) => {
  const theme = useTheme();
  const isEdit = !!(report && report.key);

  const [form, setForm] = useState(emptyReport());
  const [request, setRequest] = useState('');
  const [validation, setValidation] = useState(null);
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');          // 'draft' | 'check' | 'preview' | 'save'
  const [aiAvailable, setAiAvailable] = useState(true);

  useEffect(() => {
    if (!open) return;
    setForm(report ? { ...emptyReport(), ...report } : emptyReport());
    setRequest((report && report.request) || '');
    setValidation(null);
    setNotes('');
    setPreview(null);
  }, [open, report]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await fetch(API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_REPORTS, { headers: authHeaders() });
        if (r.ok) { const d = await r.json(); setAiAvailable(d.aiAvailable !== false); }
      } catch { /* the dialog still works by hand */ }
    })();
  }, [open]);

  const post = useCallback(async (url, body) => {
    const r = await fetch(url, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok && !d.validation) throw new Error(d.error || `${r.status}`);
    return d;
  }, []);

  const draft = async () => {
    if (!request.trim()) { notify('Describe what the report should show', 'warning'); return; }
    setBusy('draft');
    setPreview(null);
    try {
      const d = await post(API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_DRAFT, {
        request, key: isEdit ? form.key : null,
      });
      setValidation(d.validation || null);
      setNotes(d.notes || '');
      if (d.report) {
        setForm((prev) => ({ ...prev, ...d.report, request }));
        if (d.validation && d.validation.ok) notify('Draft ready - review it before saving', 'success');
      }
    } catch (e) { notify(e.message, 'error'); } finally { setBusy(''); }
  };

  const check = async () => {
    setBusy('check');
    setPreview(null);
    try {
      const d = await post(API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_VALIDATE, form);
      setValidation(d.validation || null);
      if (d.validation && d.validation.ok) notify('The warehouse accepts this query', 'success');
    } catch (e) { notify(e.message, 'error'); } finally { setBusy(''); }
  };

  const runPreview = async () => {
    setBusy('preview');
    try {
      const params = {};
      (form.parameters || []).forEach((p) => { params[p.name] = p.default ?? ''; });
      const d = await post(API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_PREVIEW, { report: form, parameters: params });
      setValidation(d.validation || null);
      setPreview(d.rows ? d : null);
      if (d.rows && d.rows.length === 0) notify('The query ran and returned no rows', 'warning');
    } catch (e) { notify(e.message, 'error'); setPreview(null); } finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save');
    try {
      const url = isEdit
        ? API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_REPORT(form.key)
        : API_ENDPOINTS.REPORTS_SCHEDULER_CUSTOM_REPORTS;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, request }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setValidation(d.validation || null);
        throw new Error(d.error || `${r.status}`);
      }
      notify(isEdit ? 'Report updated' : 'Report created', 'success');
      onSaved();
    } catch (e) { notify(e.message, 'error'); } finally { setBusy(''); }
  };

  const setParam = (i, patch) => setForm((prev) => {
    const parameters = [...(prev.parameters || [])];
    parameters[i] = { ...parameters[i], ...patch };
    return { ...prev, parameters };
  });

  const addParam = () => setForm((prev) => ({
    ...prev,
    parameters: [...(prev.parameters || []), { name: '', type: 'String', required: false, default: '', description: '' }],
  }));

  const removeParam = (i) => setForm((prev) => ({
    ...prev,
    parameters: (prev.parameters || []).filter((_, n) => n !== i),
  }));

  const errors = (validation?.issues || []).filter((i) => i.severity === 'error');
  const warnings = (validation?.issues || []).filter((i) => i.severity === 'warning');
  const canSave = !!validation && validation.ok && !!form.name.trim() && !!form.sql.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isEdit ? `Edit ${report.name}` : 'New report'}</DialogTitle>
      <DialogContent dividers>
        {/* ── describe it ── */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>What should the report show?</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            multiline minRows={2} fullWidth size="small"
            placeholder="Units and revenue by warehouse for a date range, biggest first, with a row limit"
            value={request}
            onChange={(e) => setRequest(e.target.value)}
            helperText={aiAvailable
              ? 'Plain English. The request is checked against the live warehouse schema, not guessed at.'
              : 'The AI assistant is not configured, so write the SQL below yourself. It is checked the same way.'}
          />
          <Button
            variant="contained" startIcon={busy === 'draft' ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={draft} disabled={!!busy || !aiAvailable}
            sx={{ mt: 0.25, whiteSpace: 'nowrap', bgcolor: ACCENT, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover } }}
          >
            {isEdit ? 'Redraft' : 'Analyse'}
          </Button>
        </Box>

        {/* ── what the check said ── */}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>{errors.length === 1 ? 'This cannot run' : `${errors.length} things stop this running`}</AlertTitle>
            {errors.map((i, n) => (
              <Typography key={n} variant="body2" component="div" sx={{ mb: 0.5 }}>
                • {i.message}
                {i.detail && (
                  <Typography variant="caption" component="div" sx={{ ml: 1.5, color: theme.palette.text.secondary }}>
                    {i.detail}
                  </Typography>
                )}
              </Typography>
            ))}
          </Alert>
        )}
        {warnings.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <AlertTitle>Worth knowing</AlertTitle>
            {warnings.map((i, n) => (
              <Typography key={n} variant="body2" component="div" sx={{ mb: 0.5 }}>
                • {i.message}
                {i.detail && (
                  <Typography variant="caption" component="div" sx={{ ml: 1.5, color: theme.palette.text.secondary }}>
                    {i.detail}
                  </Typography>
                )}
              </Typography>
            ))}
          </Alert>
        )}
        {validation && validation.ok && errors.length === 0 && (
          <Alert severity="success" icon={<CheckCircleIcon fontSize="small" />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              The warehouse accepts this query
              {validation.outputColumns && validation.outputColumns.length > 0
                ? ` and returns ${validation.outputColumns.length} column${validation.outputColumns.length === 1 ? '' : 's'}: `
                : '.'}
              {(validation.outputColumns || []).map((c) => c.name).join(', ')}
            </Typography>
          </Alert>
        )}
        {notes && (
          <Typography variant="caption" component="div" sx={{ mt: 1, color: theme.palette.text.secondary }}>
            {notes}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ── identity ── */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={5}>
            <TextField
              label="Name" size="small" fullWidth value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select label="Category" size="small" fullWidth value={form.category || 'Custom'}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={<Switch checked={form.enabled !== false} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />}
              label="Listed"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description" size="small" fullWidth value={form.description || ''}
              placeholder="One sentence on what a reader gets."
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Grid>
        </Grid>

        {/* ── the query ── */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Query</Typography>
        <TextField
          multiline minRows={8} fullWidth value={form.sql || ''}
          onChange={(e) => setForm({ ...form, sql: e.target.value })}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
          placeholder="SELECT ... FROM SafaviehReporting.RPTSalesInvoiceSummary WHERE InvoiceDate >= {from:Date}"
        />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Read-only SELECT. Anything a person should be able to change at run time goes in as
          <code> {'{name:Type}'} </code> and is declared below — never pasted into the SQL.
        </Typography>

        {/* ── parameters ── */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2">Parameters</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addParam}>Add parameter</Button>
        </Box>
        {(form.parameters || []).length === 0 && (
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            None. The report will run the same way every time.
          </Typography>
        )}
        {(form.parameters || []).map((p, i) => (
          <Grid container spacing={1} key={i} sx={{ mt: 0.25 }} alignItems="center">
            <Grid item xs={12} sm={2.5}>
              <TextField
                label="Name" size="small" fullWidth value={p.name || ''}
                onChange={(e) => setParam(i, { name: e.target.value })}
                InputProps={{ sx: { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                select label="Type" size="small" fullWidth value={p.type || 'String'}
                onChange={(e) => setParam(i, { type: e.target.value })}
              >
                {PARAM_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                label="Default" size="small" fullWidth value={p.default ?? ''}
                onChange={(e) => setParam(i, { default: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3.5}>
              <TextField
                label="Description" size="small" fullWidth value={p.description || ''}
                onChange={(e) => setParam(i, { description: e.target.value })}
              />
            </Grid>
            <Grid item xs={8} sm={1.5}>
              <FormControlLabel
                control={<Switch size="small" checked={p.required !== false} onChange={(e) => setParam(i, { required: e.target.checked })} />}
                label={<Typography variant="caption">Required</Typography>}
              />
            </Grid>
            <Grid item xs={4} sm={0.5}>
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => removeParam(i)}><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        ))}

        {/* ── preview ── */}
        {preview && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Preview — {preview.rowCount} row{preview.rowCount === 1 ? '' : 's'}
              {preview.truncated ? ' (first 50)' : ''}
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {(preview.columns || []).map((c) => <TableCell key={c}>{c}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {(preview.rows || []).map((row, n) => (
                    <TableRow key={n}>
                      {(preview.columns || []).map((c) => (
                        <TableCell key={c}>
                          <Typography variant="caption">{row[c] === null || row[c] === undefined ? '' : String(row[c])}</Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {isEdit && <Chip size="small" variant="outlined" label={form.key} sx={{ mr: 'auto', fontFamily: 'monospace' }} />}
        <Button onClick={onClose} disabled={!!busy}>Cancel</Button>
        <Button onClick={check} disabled={!!busy || !form.sql}>
          {busy === 'check' ? <CircularProgress size={16} /> : 'Check'}
        </Button>
        <Button startIcon={<VisibilityIcon />} onClick={runPreview} disabled={!!busy || !form.sql}>
          {busy === 'preview' ? <CircularProgress size={16} /> : 'Preview'}
        </Button>
        <Button
          variant="contained" onClick={save} disabled={!!busy || !canSave}
          sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: TOOL_COLORS.OTHER.hover } }}
        >
          {busy === 'save' ? <CircularProgress size={16} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportBuilderDialog;
